import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { EmptyState } from '../ui';

const ACCENT_KEYS = ['primary', 'secondary', 'success', 'warning'];

function getAnnualAllowance(contractType) {
  if (contractType === '30 Days Yearly') return 30;
  if (contractType === '24 Days Yearly') return 24;
  if (contractType === '21 Days Yearly') return 21;
  if (contractType === '20 Days Yearly') return 20;
  return null;
}

function normalizeBalance(balance) {
  const current = Number(balance?.current_balance || 0);
  const onHold = Number(balance?.on_hold ?? balance?.pending_requests ?? 0);
  const taken = Number(balance?.days_taken || 0);
  const projected = Number(balance?.projected_balance ?? current);

  return {
    ...balance,
    current_balance: current,
    on_hold: onHold,
    days_taken: taken,
    projected_balance: projected,
  };
}

const formatDays = (value) => `${Number(value || 0).toFixed(1)} d`;

function SummaryMetric({ label, value, color }) {
  const theme = useTheme();
  const metricColor = color || theme.palette.text.primary;

  return (
    <Box
      sx={{
        minWidth: 0,
        px: 1.2,
        py: 1,
        borderRadius: 2,
        bgcolor: alpha(metricColor, 0.07),
        border: `1px solid ${alpha(metricColor, 0.13)}`,
        minHeight: 66,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          fontWeight: 800,
          lineHeight: 1.15,
          mb: 0.35,
          textTransform: 'uppercase',
          letterSpacing: 0,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 900,
          color: metricColor,
          lineHeight: 1.15,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function BalanceValue({ label, value, primary = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'text.secondary',
          fontWeight: 800,
          lineHeight: 1.15,
          textTransform: 'uppercase',
          letterSpacing: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant={primary ? 'subtitle1' : 'body2'}
        sx={{
          color: primary ? 'text.primary' : 'text.secondary',
          fontWeight: primary ? 900 : 800,
          lineHeight: 1.25,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function LeaveBalanceRow({ balance, index, getLeaveTypeLabel }) {
  const theme = useTheme();
  const accentColor = theme.palette[ACCENT_KEYS[index % ACCENT_KEYS.length]]?.main || theme.palette.primary.main;
  const allowance = getAnnualAllowance(balance.contract_type);
  const totalCapacity = Math.max(
    allowance || 0,
    balance.current_balance + balance.on_hold + balance.days_taken,
    balance.projected_balance,
    1
  );
  const availablePct = Math.min(100, Math.max(0, (balance.current_balance / totalCapacity) * 100));
  const typeDescription = balance.leave_type === 'Annual'
    ? allowance
      ? `${formatDays(allowance)} yearly allowance`
      : 'Core yearly balance'
    : 'Leave bucket';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.2, md: 1.35 },
        borderRadius: 2.25,
        borderColor: alpha(accentColor, 0.14),
        bgcolor: alpha(theme.palette.background.paper, 0.92),
        transition: theme.transitions.create(['border-color', 'background-color'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': {
          borderColor: alpha(accentColor, 0.34),
          bgcolor: alpha(accentColor, 0.035),
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'minmax(180px, 1.5fr) repeat(4, minmax(86px, 0.7fr))',
          },
          gap: { xs: 1, sm: 1.25 },
          alignItems: 'center',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 8,
              height: 36,
              borderRadius: 999,
              bgcolor: accentColor,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 900, color: 'text.primary', lineHeight: 1.2 }}
              noWrap
              title={getLeaveTypeLabel(balance.leave_type)}
            >
              {getLeaveTypeLabel(balance.leave_type)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              {typeDescription}
            </Typography>
          </Box>
        </Stack>

        <BalanceValue label="Available" value={formatDays(balance.current_balance)} primary />
        <BalanceValue label="Reserved" value={formatDays(balance.on_hold)} />
        <BalanceValue label="Taken" value={formatDays(balance.days_taken)} />
        <BalanceValue label="Year-end" value={formatDays(balance.projected_balance)} />
      </Box>

      <Box
        aria-hidden
        sx={{
          mt: 1.1,
          height: 5,
          borderRadius: 999,
          bgcolor: alpha(theme.palette.divider, 0.55),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${availablePct}%`,
            height: '100%',
            borderRadius: 'inherit',
            bgcolor: accentColor,
          }}
        />
      </Box>
    </Paper>
  );
}

export default function LeaveBalancesPanel({
  balances = [],
  contractType,
  getLeaveTypeLabel = (type) => type || '-',
  emptyTitle = 'No balances',
  emptyDescription = 'No leave balances found.',
}) {
  const theme = useTheme();

  const normalizedBalances = useMemo(
    () =>
      balances
        .map((balance) => ({
          ...normalizeBalance(balance),
          contract_type: contractType,
        }))
        .sort((left, right) => {
          if (left.leave_type === 'Annual') return -1;
          if (right.leave_type === 'Annual') return 1;
          return right.current_balance - left.current_balance;
        }),
    [balances, contractType]
  );

  if (!normalizedBalances.length) {
    return (
      <EmptyState
        compact
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  const totalCurrent = normalizedBalances.reduce((sum, item) => sum + item.current_balance, 0);
  const totalReserved = normalizedBalances.reduce((sum, item) => sum + item.on_hold, 0);
  const totalTaken = normalizedBalances.reduce((sum, item) => sum + item.days_taken, 0);
  const totalProjected = normalizedBalances.reduce((sum, item) => sum + item.projected_balance, 0);
  const annualBalance = normalizedBalances.find((item) => item.leave_type === 'Annual') || null;
  const annualAllowance = getAnnualAllowance(contractType);
  const annualUsagePct = annualBalance && annualAllowance
    ? Math.max(0, Math.min(100, Math.round((annualBalance.days_taken / annualAllowance) * 100)))
    : null;

  return (
    <Stack spacing={1.2}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 1.35, md: 1.6 },
          borderRadius: 2.5,
          borderColor: alpha(theme.palette.primary.main, 0.14),
          bgcolor: alpha(theme.palette.background.paper, 0.94),
        }}
      >
        <Stack
          spacing={1.25}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 0,
              }}
            >
              Available now
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.2, fontWeight: 900, lineHeight: 1.05 }}>
              {totalCurrent.toFixed(1)} days
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
              {normalizedBalances.length} leave type{normalizedBalances.length > 1 ? 's' : ''} with current, reserved,
              taken, and year-end balances.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 0.9,
            }}
          >
            <SummaryMetric label="Reserved" value={formatDays(totalReserved)} color={theme.palette.warning.main} />
            <SummaryMetric label="Taken" value={formatDays(totalTaken)} color={theme.palette.info.main} />
            <SummaryMetric label="Year-end" value={formatDays(totalProjected)} color={theme.palette.success.main} />
            <SummaryMetric
              label="Annual used"
              value={annualUsagePct === null ? 'N/A' : `${annualUsagePct}%`}
              color={theme.palette.secondary.main}
            />
          </Box>
        </Stack>

        {annualBalance && annualAllowance ? (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontWeight: 700 }}>
            Annual leave: {formatDays(annualBalance.current_balance)} available from a {formatDays(annualAllowance)}{' '}
            yearly allowance.
          </Typography>
        ) : null}
      </Paper>

      <Stack spacing={0.85}>
        {normalizedBalances.map((balance, index) => (
          <LeaveBalanceRow
            key={balance.leave_type}
            balance={balance}
            index={index}
            getLeaveTypeLabel={getLeaveTypeLabel}
          />
        ))}
      </Stack>
    </Stack>
  );
}
